import React, { memo, useEffect, useState } from 'react';
import styled from 'styled-components';
import theme from '@styles/theme';
import BarChart from '@components/feature/Dashboard/BarChart';

type StickChartProps = {
    DOUBLE_LINE?: boolean;
    title?: string;
    subTitle?: string;
    data: ChartDataType[];
};
type ChartDataType = {
    name: string;
    value: number;
    value1?: number;
};

const StickChart = ({
    DOUBLE_LINE,
    data = [],
    title,
    subTitle
}: StickChartProps) => {
    const [chartData, setChartData] = useState<ChartDataType[]>([
        { name: '1월', value: 0 },
        { name: '2월', value: 0 },
        { name: '3월', value: 0 },
        { name: '4월', value: 0 },
        { name: '5월', value: 0 },
        { name: '6월', value: 0 },
        { name: '7월', value: 0 },
        { name: '8월', value: 0 },
        { name: '9월', value: 0 },
        { name: '10월', value: 0 },
        { name: '11월', value: 0 },
        { name: '12월', value: 0 }
    ]);

    useEffect(() => {
        if (DOUBLE_LINE) {
            const maxVisitor = Math.max(
                ...data.map((item: ChartDataType) => item.value)
            );
            const maxParking = Math.max(
                ...data.map((item: ChartDataType) => item.value1 || 0)
            );
            const max = Math.max(maxVisitor, maxParking);

            const result = data.map((item: ChartDataType) => ({
                name: item.name,
                value: (item.value / max) * 100,
                value1: ((item.value1 || 1) / max) * 100
            }));

            setTimeout(() => {
                setChartData(result);
            }, 100);
            return;
        }

        const max = Math.max(...data.map((item: ChartDataType) => item.value));
        const result = data.map((item: ChartDataType) => ({
            name: item.name,
            value: (item.value / max) * 100
        }));

        setTimeout(() => {
            setChartData(result);
        }, 100);
    }, [data]);

    if (DOUBLE_LINE) {
        return (
            <Wrapper>
                <Title>{title}</Title>
                <ColorInfo>
                    <Dot />
                    <ColorText $marginRight={20}>방문자수</ColorText>
                    <Dot $bgColor={theme.colors.lightGrayFontColor} />
                    <ColorText>주차요청</ColorText>
                </ColorInfo>
                <ChartBox>
                    {chartData.map((data: ChartDataType, index: number) => {
                        return (
                            <StickLine key={`${index}-stick`}>
                                <DoubleStickBox>
                                    <StickBox>
                                        <Stick $height={data.value || 0} />
                                    </StickBox>
                                    <StickBox>
                                        <Stick
                                            $height={data.value1 || 0}
                                            $bgColor={
                                                theme.colors.lightGrayFontColor
                                            }
                                        />
                                    </StickBox>
                                </DoubleStickBox>
                                <ValueText>{data.name}</ValueText>
                            </StickLine>
                        );
                    })}
                </ChartBox>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <Title>{title}</Title>
            <SubTitle>{subTitle}</SubTitle>
            <ChartBox>
                <BarChart data={data} />
            </ChartBox>
        </Wrapper>
    );
};

export default memo(StickChart);

const Wrapper = styled.div`
    flex: 1;
`;
const Title = styled.div`
    ${theme.typography.caption};
    font-family: PretendardSemiBold, sans-serif;
`;
const SubTitle = styled.div`
    ${theme.typography.caption};
    color: ${theme.colors.deepGrayFontColor};
    margin: 8px 0 20px;
`;
const ChartBox = styled.div`
    height: 170px;
    display: flex;
`;
const StickLine = styled.div`
    height: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    position: relative;

    border: 1px solid red;
`;
const DoubleStickBox = styled.div`
    height: 100%;
    display: flex;
    align-items: flex-end;
    gap: 0 4px;
`;
const StickBox = styled.div<{ $height?: string }>`
    height: ${({ $height }) => ($height ? $height : '100%')};
    display: flex;
    align-items: flex-end;
`;
const Stick = styled.div<{ $height?: number; $bgColor?: string }>`
    width: 12px;
    height: ${({ $height }) => ($height ? $height : 0)}%;
    max-height: 100%;
    min-height: 0;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    transition: height 0.5s;
    background-color: ${({ $bgColor }) =>
        $bgColor ? $bgColor : theme.colors.blackColor};
`;
const ValueText = styled.div`
    height: 36px;
    display: flex;
    align-items: flex-end;
`;
const ColorInfo = styled.div`
    display: flex;
    align-items: center;
    margin: 8px 0 20px;
`;
const Dot = styled.div<{ $bgColor?: string }>`
    width: 9px;
    height: 9px;
    margin-right: 4px;
    border-radius: 50%;
    background-color: ${({ $bgColor }) =>
        $bgColor ? $bgColor : theme.colors.blackColor};
`;
const ColorText = styled.div<{ $marginRight?: number }>`
    font-size: 14px;
    color: ${theme.colors.deepGrayFontColor};
    margin-right: ${({ $marginRight }) => ($marginRight ? $marginRight : 0)}px;
`;
